<script setup lang="ts">
import {computed, onMounted, ref, watch} from 'vue'
import type {ReturnBatch} from '@/types/batch-types'
import SupplierInput from '@/components/features/batches/form-elements/SupplierInput.vue'
import TotalWeightInput from '@/components/features/batches/form-elements/TotalWeightInput.vue'
import TotalLengthInput from '@/components/features/batches/form-elements/TotalLengthInput.vue'
import NotesInput from '@/components/features/batches/form-elements/NotesInput.vue'
import LastEditedByInput from '@/components/features/batches/form-elements/LastEditedByInput.vue'
import TabPanel from 'primevue/tabpanel'
import TabView from 'primevue/tabview'
import Tag from 'primevue/tag'
import Button from 'primevue/button'
import tabviewStyle2 from '@/presets/lara/tabview/tabviewStyle2'
import {Icon} from '@iconify/vue'
import ReturnFromSupplierEditRollsList from '@/components/features/batches/form-elements/ReturnFromSupplierEditRollsList.vue'
import QualityStatusDropdown from '@/components/features/batches/form-elements/QualityStatusDropdown.vue'
import TesterInput from '@/components/features/batches/form-elements/TesterInput.vue'
import FormCalendar from '@/components/inputs/FormCalendar.vue'
import FireTestingStatusDropdown from '@/components/features/batches/form-elements/FireTestingStatusDropdown.vue'
import DateReturnedInput from '@/components/features/batches/form-elements/DateReturnedInput.vue'
import ReturnSupplierInput from '@/components/features/batches/form-elements/ReturnSupplierInput.vue'
import {type Product} from "@/types/product-types";
import {getProductById, getProductList} from "@/api/products";
import TotalLengthUnit from '@/components/features/batches/form-elements/TotalLengthUnit.vue'
import BatchNumberInput from "@/components/features/batches/form-elements/BatchNumberInput.vue";
import {getBatchById} from "@/api/batches";

const props = defineProps<{
  disabled: boolean
  type: 'add' | 'view'
  modelValue: ReturnBatch
  fabricYieldValue?: number
  fabricYieldUnit?: number
  batchId?: string
}>()

const activeTabIndex = ref(0)
const qualities = ref<Product[]>([]);
const previousBatchRolls = ref<any[]>([]);
const isUpdating = ref(false);

const emit = defineEmits<{
  (event: 'update:modelValue', value: ReturnBatch): void;
}>();

const model = ref<ReturnBatch>({
  ...props.modelValue,
  rolls: props.modelValue.rolls || [],
  finishingQualityId: props.modelValue.finishingQualityId,
  batchNumber: props.modelValue.batchNumber || '',
})

const getPreviousBatchNumber = async () => {
  if (props.batchId) {
    const batch = await getBatchById(props.batchId);
    model.value.batchNumber = batch.batchNumber || '';
  }
}

watch(() => props.modelValue, (newValue) => {
  if (!isUpdating.value) {
    model.value = {
      ...newValue,
      rolls: newValue.rolls || [],
      finishingQualityId: newValue.finishingQualityId,
      batchNumber: newValue.batchNumber || '',
    }
  }
}, { deep: true })

const filteredRolls = computed(() => {
  if (!model.value.rolls) return []

  return model.value.rolls.filter(roll => {
    if (!roll.id || roll.rollState === undefined) return true

    const rollsSentToSupplier = previousBatchRolls.value?.filter(availableRoll =>
        availableRoll.isActive && availableRoll.rollState === 1
    ) || []

    return rollsSentToSupplier.some(sentRoll => sentRoll.id === roll.id)
  })
})

const rolls = computed({
  get() {
    return filteredRolls.value
  },
  set(value) {
    if (isUpdating.value) return;

    isUpdating.value = true;

    const validRolls = value.filter(roll => {
      if (!roll.id || roll.rollState === undefined) return true

      const rollsSentToSupplier = previousBatchRolls.value?.filter(availableRoll =>
          availableRoll.isActive && availableRoll.rollState === 1
      ) || []

      return rollsSentToSupplier.some(sentRoll => sentRoll.id === roll.id)
    })

    model.value.rolls = validRolls;

    const totalLengthValue = validRolls.reduce((sum, roll) => sum + (roll.lengthValue || 0), 0)
    const totalWeight = validRolls.reduce((sum, roll) => sum + (roll.weight || 0), 0)

    model.value.lengthValue = totalLengthValue;
    model.value.weight = totalWeight;

    emit('update:modelValue', {
      ...model.value,
      rolls: validRolls,
      lengthValue: totalLengthValue,
      weight: totalWeight
    })

    setTimeout(() => {
      isUpdating.value = false;
    }, 0);
  }
});

const handleUpdateRolls = (updatedRolls: ReturnBatch['rolls']) => {
  if (isUpdating.value) return;

  if (Array.isArray(updatedRolls)) {
    const validRolls = updatedRolls.filter(roll => {
      if (!roll.id || roll.rollState === undefined) return true

      const rollsSentToSupplier = previousBatchRolls.value?.filter(availableRoll =>
          availableRoll.isActive && availableRoll.rollState === 1
      ) || []

      return rollsSentToSupplier.some(sentRoll => sentRoll.id === roll.id)
    })

    if (props.fabricYieldValue !== undefined && props.fabricYieldValue > 0) {
      rolls.value = validRolls.map(roll => {
        if (roll.weight) {
          return {
            ...roll,
            lengthValue: roll.weight * props.fabricYieldValue!,
            lengthUnit: props.fabricYieldUnit || 0
          };
        }
        return roll;
      });
    } else {
      rolls.value = validRolls;
    }
  } else {
    console.error("Invalid updatedRolls:", updatedRolls);
  }
};

const handleTotalsCalculated = ({ weight, lengthValue, totalPercentageWeightLostOrGained  }: {
  weight: number;
  lengthValue: number
  totalPercentageWeightLostOrGained: number
}) => {
  if (isUpdating.value) return;

  isUpdating.value = true;

  model.value.lengthValue = lengthValue
  model.value.weight = weight;
  model.value.totalPercentageWeightLostOrGained = totalPercentageWeightLostOrGained;

  emit('update:modelValue', { ...model.value });

  setTimeout(() => {
    isUpdating.value = false;
  }, 0);
};

const handleBatchNumberUpdate = (newBatchNumber: string) => {
  if (isUpdating.value) return;

  model.value.batchNumber = newBatchNumber;
  emit('update:modelValue', { ...model.value });
};

const qualityName = computed(() => {
  return qualities.value?.find((quality) => quality.id == model.value.finishingQualityId)?.finishedReference || 'Unknown Quality';
})

const fetchPreviousBatchRolls = async () => {
  if (props.batchId) {
    try {
      const batch = await getBatchById(props.batchId);
      if (batch && batch.rolls) {
        previousBatchRolls.value = batch.rolls;

        if (model.value.rolls && model.value.rolls.length > 0) {
          const rollsSentToSupplier = batch.rolls.filter(roll =>
              roll.isActive && roll.rollState === 1
          );

          model.value.rolls = model.value.rolls.filter(roll => {
            if (!roll.id) return true

            return rollsSentToSupplier.some(sentRoll => sentRoll.id === roll.id)
          });
        }
      }
    } catch (error) {
      console.error("Error fetching previous batch rolls:", error);
    }
  }
};

const fetchProductAndCalculateRollLengths = async () => {
  if (isUpdating.value) return;

  if (model.value.finishingQualityId) {
    try {
      const product = await getProductById(model.value.finishingQualityId);
      if (product && product.fabricYieldValue) {
        if (model.value.rolls && model.value.rolls.length > 0) {
          rolls.value = model.value.rolls.map(roll => {
            if (roll.weight) {
              return {
                ...roll,
                lengthValue: roll.weight * product.fabricYieldValue!,
                lengthUnit: product.fabricYieldUnit || 0
              };
            }
            return roll;
          });
        }
      }
    } catch (error) {
      console.error("Error fetching product details:", error);
    }
  }
};


onMounted(async () => {
  qualities.value = await getProductList();
  await getPreviousBatchNumber();
  await fetchPreviousBatchRolls();
})

</script>

<template>
  <div class="grid grid-cols-12 gap-20 gap-y-4">
    <div class="col-span-12 mb-6">
      <h2 class="font-semibold text-lg mb-4">New Batch Information</h2>
      <div class="grid grid-cols-12 gap-4">
        <BatchNumberInput
            class="col-span-6"
            input-name="batchNumber"
            v-model="model.batchNumber"
            @update:modelValue="handleBatchNumberUpdate"
            :disabled="disabled"
        />
      </div>
    </div>

    <div class="col-span-12 flex items-center gap-5 mb-5">
      <span class="text-lg font-bold">
        Rolls Being Returned ({{ filteredRolls.length || 0 }})
      </span>

      <RouterLink
          :to="`/productSpecification/${model.finishingQualityId}`"
          class="flex gap-2 items-center bg-green-100 text-green-600 py-1 px-3 rounded-lg hover:bg-green-200 transition-colors"
      >
        <Icon icon="mdi:fabric" class="text-xl" />
        {{ qualityName }}
      </RouterLink>
    </div>

    <div class="col-span-12">
      <h2 class="font-semibold text-lg my-5">Batch Details</h2>
      <div >
        <div class="grid grid-cols-12 gap-20 gap-y-4">
          <ReturnFromSupplierEditRollsList
              v-model="rolls"
              @totalsCalculated="handleTotalsCalculated"
              @update:modelValue="handleUpdateRolls"
              class="col-span-12"
              :includeSupplierNo="true"
              lengthLabel="New Length"
              weightLabel="Roll Weight"
              :show-actions="false"
              :disabled="disabled"
              :fabricYieldValue="props.fabricYieldValue"
              :fabricYieldUnit="props.fabricYieldUnit"
              :availableRolls="previousBatchRolls"
              :allowAddRolls="true"
          />

          <DateReturnedInput
            class="col-span-6"
            input-name="dateReturned"
            v-model="model.dateReturned"
            :disabled="disabled"
          />
          <ReturnSupplierInput
            class="col-span-6"
            input-name="finishingSupplierId"
            v-model="model.finishingSupplierId"
            :disabled="true"
          />
          <div class="flex items-center gap-4 w-full col-span-6">
            <TotalLengthInput
              :disabled="true"
              input-name="totalLengthValue"
              v-model="model.lengthValue"
            />
            <TotalLengthUnit input-name="totalLengthUnit" v-model="model.lengthUnit" :disabled="true" />
          </div>
          <TotalWeightInput
            class="col-span-6"
            v-model="model.weight"
            input-name="totalWeight"
            :disabled="true"
          />
          <NotesInput
            class="col-span-12"
            input-name="notes"
            v-model="model.notes"
            :disabled="disabled"
          />
          <LastEditedByInput class="col-span-6" input-name="lastEditedBy" />
        </div>
  </div>
</div>
</div>
</template>

<style scoped></style>
